import React, {useEffect, useRef} from 'react';
import {createValueTween} from '../lib/three/tweens';
import {Easing, Tween} from '@tweenjs/tween.js';
import Logger from '../lib/Logger';
import {PortalWrapper} from "./PortalWrapper";

const logger = new Logger("ScDrawer");

interface TouchData {
    xDiff: number
    xDown: number
    yDown: number
    transformX: number
    dragDirection: "horizontal" | "vertical" | undefined
    dragStartTime: number
    currentTransformX: number
}

interface IDrawerProps {
    children: React.ReactElement<any, string>;
    // container?: React.RefObject<HTMLElement>;
    rightVisibleEdgeWidth?: number;
    leftVisibleEdgeWidth?: number;
    leftOpen?: boolean;
    rightOpen?: boolean;
    onLeftOpen: () => void;
    onRightOpen: () => void;
    onClose: () => void;
}

const ScDrawer: React.FC<IDrawerProps> = (
        {
            children,
            // container: containerRef,
            rightVisibleEdgeWidth = 0,
            leftVisibleEdgeWidth = 0,
            leftOpen,
            rightOpen,
            onLeftOpen,
            onRightOpen,
            onClose,
        }) => {

    const tweenRef = useRef<Tween<{ x: number }>>();


    const childRef = useRef<HTMLDivElement>();
    const drawerViewportRef = useRef<HTMLDivElement>(null);

    const touchDataRef = useRef<TouchData>({
        xDiff: 0,
        xDown: 0,
        yDown: 0,
        transformX: 0,
        dragDirection: undefined,
        dragStartTime: 0,
        currentTransformX: 0,
    });

    const touchData = touchDataRef.current;


    // useEffect(() => {
    //     const viewport = drawerViewportRef.current;
    //
    //     if (viewport) {
    //         viewport.ontouchstart = handleTouchStart;
    //         viewport.ontouchmove = handleTouchMove;
    //         viewport.ontouchend = handleTouchEnd;
    //
    //         return () => {
    //             viewport.ontouchstart = null;
    //             viewport.ontouchmove = null;
    //             viewport.ontouchend = null;
    //         }
    //     }
    //
    // }, [drawerViewportRef.current]);

    useEffect(() => {
        if (!drawerViewportRef.current) {
            return;
        }

        const maxTransformToRight = drawerViewportRef.current.clientWidth - leftVisibleEdgeWidth;
        const maxTransformToLeft = -(drawerViewportRef.current.clientWidth - rightVisibleEdgeWidth);

        const {
            xDiff,
            transformX,
        } = touchData;

        touchData.xDown = 0;
        touchData.yDown = 0;

        const currentTransformX = transformX + xDiff

        if (tweenRef.current) {
            tweenRef.current.stop();
        }

        let targetValue;

        if (leftOpen) {
            targetValue = maxTransformToRight;
        } else if (rightOpen) {
            targetValue = maxTransformToLeft;
        } else {
            targetValue = 0;
        }

        tweenRef.current = createSlidingTween(currentTransformX, targetValue);
        tweenRef.current.start();

    }, [drawerViewportRef.current, leftOpen, rightOpen]);


    function getTouches(evt: TouchEvent) {
        // @ts-ignore
        return evt.touches || evt.originalEvent.touches;
    }

    // function handleTouchStart(evt: TouchEvent) {
    function handleTouchStart(evt: React.TouchEvent) {
        logger.trace("1.      touchStart: %o", touchData);

        if (tweenRef.current) {
            tweenRef.current.stop();
        }

        const firstTouch = getTouches(evt.nativeEvent)[0];
        touchData.xDown = firstTouch.clientX;
        touchData.yDown = firstTouch.clientY;
        touchData.dragDirection = undefined;
        touchData.dragStartTime = Date.now()

        //@todo fix this logic
        touchData.currentTransformX = touchData.transformX;
    }

    // function handleTouchEnd(evt: TouchEvent) {
    function handleTouchEnd(evt: React.TouchEvent) {

        const xUp = evt.changedTouches[0].clientX;
        const {
            xDown,
            dragStartTime,
        } = touchData;

        const xDiff = xUp - xDown;

        touchData.xDown = 0;
        touchData.xDiff = 0;
        touchData.yDown = 0;

        if (!drawerViewportRef.current) {
            return;
        }

        const clientWidth = drawerViewportRef.current.clientWidth

        const maxTransformToRight = drawerViewportRef.current.clientWidth - leftVisibleEdgeWidth;
        const maxTransformToLeft = -(drawerViewportRef.current.clientWidth - rightVisibleEdgeWidth);
        let transformX: number;
        const newTransformX = touchData.transformX + xDiff;

        if (checkCanDragX(newTransformX)) {
            transformX = newTransformX
        } else {
            transformX = touchData.currentTransformX;
        }

        const dragTime = Date.now() - dragStartTime;
        const dragSpeed = Math.abs(xDiff / dragTime);
        const maxPxPerMs = 0.25;

        // count++;
        // if(count === 2){
        //   console.log("d");
        // }

        logger.trace("1.      touchEnd: %o", {
            xUp,
            maxTransformToRight,
            maxTransformToLeft,
            ...touchData
        });

        const maxDrag = clientWidth / 2 - 0.15 * clientWidth;
        const passedRightBound = transformX < -maxDrag;
        const passedLeftBound = transformX > maxDrag;
        const isInMid = !(passedLeftBound || passedRightBound);
        const draggingRight = xDiff > 0;
        const draggingLeft = !draggingRight;
        const fastDrag = dragSpeed > maxPxPerMs;
        const passedRight = transformX < maxTransformToLeft;
        const passedLeft = transformX > maxTransformToRight;

        let leftOpen, rightOpen = false;

        //dragDirection === undefined means no touch move occurred. user could have stopped the drawer, it should respond
        // and slide to its next logical position
        const isCentered = transformX === 0;
        if (!(touchData.dragDirection === "horizontal" || touchData.dragDirection === undefined) && isCentered) {
            return;
        }

        if (passedLeftBound && (fastDrag && draggingLeft)) {

        } else if (isInMid && fastDrag && draggingLeft) {
            rightOpen = true;
        } else if (isInMid && fastDrag && draggingRight) {
            leftOpen = true;
        } else if (passedRightBound && fastDrag && draggingRight) {
        } else if (passedLeftBound) {
            leftOpen = true;
            logger.warn("passedLeftBound. passedLeft %s", passedLeft)
        } else if (passedRightBound) {
            rightOpen = true;
            logger.warn("passedRightBound. passedRight %s", passedRight)
        } else if (isInMid) {
        } else if (passedLeft) {
            logger.error("passedLeft. shouldnt have taken this branch")
            leftOpen = true;
        } else if (passedRight) {
            logger.error("rightOpen. shouldnt have taken this branch")
            rightOpen = true;
        }

        let targetValue;
        if (leftOpen) {
            targetValue = maxTransformToRight;
            tweenRef.current = createSlidingTween(transformX, targetValue).onComplete(onLeftOpen);
            tweenRef.current.start();
        } else if (rightOpen) {
            targetValue = maxTransformToLeft;
            tweenRef.current = createSlidingTween(transformX, targetValue).onComplete(onRightOpen);
            tweenRef.current.start();
        } else {
            targetValue = 0;
            tweenRef.current = createSlidingTween(transformX, targetValue).onComplete(onClose);
            tweenRef.current.start();
        }

    }

    function createSlidingTween(transformX: number, targetValue: number) {
        if (!drawerViewportRef.current) {
            throw new Error("drawerViewportRef.current not set set")
        }

        const clientWidth = drawerViewportRef.current.clientWidth;
        const delta = targetValue - transformX;
        const distance = Math.abs(delta);
        const factor = Math.max(1, Math.min(3, 100 / distance));

        return createValueTween(
                transformX,
                targetValue,
                {
                    easing: Easing.Elastic.Out,
                    duration: 1400 * distance / clientWidth * factor,
                    onUpdate: (value: number) => {
                        touchData.transformX = value;
                        if (!childRef.current) {
                            throw new Error("childRef.current not set")
                        }
                        childRef.current.style["transform"] = `translate3d(${touchData.transformX}px, 0, 0)`;

                    }
                }
        );
    }

    //@todo  getTouches?
    // function handleTouchMove(evt: TouchEvent) {
    function handleTouchMove(evt: React.TouchEvent) {
        let {
            xDown,
            yDown,
            transformX,
        } = touchData;

        if (!xDown || !yDown) {
            return;
        }

        const xUp = evt.touches[0].clientX;
        const yUp = evt.touches[0].clientY;

        const xDiff = xUp - xDown;
        const yDiff = yUp - yDown;

        touchData.xDiff = xDiff;

        logger.trace("1.      touchMove: %o", touchData);

        if (!touchData.dragDirection) {
            touchData.dragDirection = Math.abs(xDiff) > Math.abs(yDiff) ? "horizontal" : "vertical";
        }

        const currentTransformX = transformX + xDiff;

        if (checkCanDragX(currentTransformX)) {
            if (childRef.current) {
                childRef.current.style["transform"] = `translate3d(${currentTransformX}px, 0, 0)`;
            }

            touchData.currentTransformX = currentTransformX;
        }

    }

    function checkCanDragX(currentTransformX: number) {
        if (!drawerViewportRef.current) {
            return false;
        }
        const maxTransformToRight = drawerViewportRef.current.clientWidth - leftVisibleEdgeWidth;
        const maxTransformToLeft = -(drawerViewportRef.current.clientWidth - rightVisibleEdgeWidth);
        const passedDraggableBoundary = currentTransformX < maxTransformToLeft || currentTransformX > maxTransformToRight
        return touchData.dragDirection === "horizontal" && !passedDraggableBoundary
    }

    // const container = containerRef.current;

    return <PortalWrapper>
        <div
                ref={drawerViewportRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                    // position: container ? "static" : "fixed",
                    position: "fixed",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                }}>
            {/* <Box position={"absolute"} width={"100%"} zIndex={1000} padding={"30px"} row center>{dragSpeed}</Box> */}
            {React.cloneElement(children, {ref: childRef})}
        </div>
    </PortalWrapper>

};

export default ScDrawer;
